import React, { useEffect, useRef } from 'react';
import { FiArrowRight } from "react-icons/fi";
import { cta, s1 } from "../assets";
import { Reveal } from "./reveal";
import { SplitText } from "gsap/SplitText";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from 'react-router-dom';

const Landing = () => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const mousePos = useRef({ x: -1000, y: -1000 });
  const animationFrame = useRef(null);
  
  // Parallax scroll effect
  const { scrollY } = useScroll();
  const imageY = useTransform(scrollY, [0, 1000], [0, 200]); // Image moves down
  const imageScale = useTransform(scrollY, [0, 1000], [1, 1.15]); // Image scales up
  const overlayOpacity = useTransform(scrollY, [0, 600], [1, 0.8]); // Overlay fades slightly
  const textY = useTransform(scrollY, [0, 800], [0, -100]); // Text moves up
  const rightTextY = useTransform(scrollY, [0, 800], [0, -50]); // Right text moves up

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const container = containerRef.current;
    
    let width, height;
    let dots = [];
    let time = 0;

    const resizeCanvas = () => {
      width = container.offsetWidth;
      height = container.offsetHeight;
      canvas.width = width;
      canvas.height = height;
      generateDots();
    };

    const generateDots = () => {
      dots = [];
      const spacing = 3.5; // Much smaller spacing for tiny dots
      const rows = Math.ceil(height / spacing) + 20;
      const cols = Math.ceil(width / spacing) + 20;

      for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
          dots.push({
            x: j * spacing,
            y: i * spacing,
            baseX: j * spacing,
            baseY: i * spacing,
            radius: 1.2, // Very small radius
            baseRadius: 1.2,
            spacing: spacing,
            offset: Math.random() * Math.PI * 2,
          });
        }
      }
    };

    // Initialize
    resizeCanvas();

    // Event listeners
    window.addEventListener('resize', resizeCanvas);

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, []);

  return (
    <div className={`relative bg-white min-h-screen w-full`}>
        <div 
          ref={containerRef}
          className="relative w-full h-screen overflow-hidden cursor-crosshair"
          style={{ background: '#f0f0f0' }}
          >
          {/* Image with parallax effect */}
          <motion.img 
              src={s1} 
              alt="" 
              className="w-full h-full object-cover absolute inset-0"
              style={{ 
                y: imageY,
                scale: imageScale,
                willChange: 'transform'
              }}
          />
          
          {/* Blue overlay filter with parallax opacity */}
          <motion.div 
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.87)',
                mixBlendMode: 'multiply',
                opacity: overlayOpacity
              }}
          />
          
          {/* Halftone pattern - FIXED, no scroll movement */}
          <div
            className="absolute inset-0 w-full h-full"
          >
            <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full pointer-events-auto"
                style={{ mixBlendMode: 'multiply' }}
            />
          </div>
          
          {/* Additional blue tint overlay with parallax opacity */}
          <motion.div 
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={{ 
                backgroundColor: 'rgba(237, 241, 247, 0.5)',
                mixBlendMode: 'overlay',
                opacity: overlayOpacity
              }}
          />
        </div> 

        {/* Left content with parallax effect */}
        <motion.div 
          style={{ y: textY }}
          className="absolute md:left-20 left-0 bottom-20 px-10 z-10 flex flex-col gap-2 pt-40"
        >
            <Reveal>
                <h2 className="py-6 md:text-[100px] text-[60px] font-lato font-bold text-fourth md:max-w-[800px] max-w-[360px]">
                    Aegis Education Group
                </h2>
            </Reveal>
            <Reveal>
              <Link to="/Expertise">
                <motion.div 
                initial="initial"
                whileHover="hovered"
                className="flex flex-row items-center justify-center gap-2 cursor-pointer group relative overflow-hidden"
                >
                {/* Arrow icon container with animation */}
                <div className="w-[40px] h-[40px] relative mr-2 overflow-hidden">
                    <motion.div 
                    variants={{
                        initial: { y: 0 },
                        hovered: { y: "-100%" },
                    }}
                    className="absolute inset-0 bg-second flex items-center justify-center"
                    >
                    <FiArrowRight size={20} className="text-fourth" />
                    </motion.div>
                    <motion.div 
                    variants={{
                        initial: { y: "100%" },
                        hovered: { y: 0 },
                    }}
                    className="absolute inset-0 bg-fourth flex items-center justify-center"
                    >
                    <FiArrowRight size={20} className="text-second" />
                    </motion.div>
                </div>
                
                {/* Text with animation */}
                <div className="relative overflow-hidden">
                    <motion.h2 
                    variants={{
                        initial: { y: 0 },
                        hovered: { y: "-100%" },
                    }}
                    className="md:text-primary text-fourth xs:text-sm text-mm font-lato font-semibold"
                    >
                    What We Do
                    </motion.h2>
                    <motion.h2 
                    variants={{
                        initial: { y: "100%" },
                        hovered: { y: 0 },
                    }}
                    className="absolute inset-0 text-second xs:text-sm text-mm font-lato font-semibold"
                    >
                    What We Do
                    </motion.h2>
                </div>
                </motion.div>
              </Link>
            </Reveal>
        </motion.div>

        {/* Right content with parallax effect */}
        <motion.div 
          style={{ y: rightTextY }}
          className="absolute md:right-20 right-0 bottom-2 px-10 z-2 flex flex-col items-end pt-20 w-full"
        >
            <span className="border-l border-1 border-second h-[400px]"></span>                
            <Reveal>
                <h2 className="py-6 xs:text-mm text-[30px] font-lato font-medium md:max-w-[400px] max-w-[360px] md:text-primary text-fourth">
                    Safeguarding, culture and staff practice support for international schools.
                </h2>
            </Reveal>
            <span className="border-l border-1 border-second h-[400px]"></span>
        </motion.div> 
    </div>
)};

export default Landing;